import { FC, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { TradingCounterPartySummary, TraderGrantDto } from "../../../api/Api";
import { useCounterpartyOverview } from "../../tradingCounterparties/dataHooks/counterpartyQueries";
import {
  useGrantTraderAccess,
  useRevokeTraderAccess,
  useTraderGrants,
  useUpdateTraderAccessExpiry
} from "./queries";
import { GrantDialog } from "../../../components/traderAccess/grantDialog";
import { GrantsTable } from "../../../components/traderAccess/grantsTable";
import { ExtendExpiryDialog } from "../../../components/traderAccess/extendExpiryDialog";
import { RevokeConfirmDialog } from "../../../components/traderAccess/revokeConfirmDialog";

export const TraderAccessAdmin: FC = () => {
  const [selectedCounterparty, setSelectedCounterparty] =
    useState<TradingCounterPartySummary | null>(null);
  const [includeRevoked, setIncludeRevoked] = useState(false);
  const [grantOpen, setGrantOpen] = useState(false);
  const [extendTarget, setExtendTarget] = useState<TraderGrantDto | null>(null);
  const [revokeTarget, setRevokeTarget] = useState<TraderGrantDto | null>(null);

  const counterpartiesQuery = useCounterpartyOverview("active", "include");
  const counterpartyOptions = useMemo(
    () => counterpartiesQuery.data ?? [],
    [counterpartiesQuery.data]
  );

  const counterpartyId = selectedCounterparty?.id;

  const grantsQuery = useTraderGrants(counterpartyId, includeRevoked);
  const grants = grantsQuery.data ?? [];

  const grantMutation = useGrantTraderAccess({
    onSuccess: () => setGrantOpen(false)
  });
  const revokeMutation = useRevokeTraderAccess({
    onSuccess: () => setRevokeTarget(null)
  });
  const updateExpiryMutation = useUpdateTraderAccessExpiry({
    onSuccess: () => setExtendTarget(null)
  });

  const handleClearExpiry = (row: TraderGrantDto) => {
    if (!row.entityId || !row.userObjectId) return;
    updateExpiryMutation.mutate({
      entityId: row.entityId,
      userObjectId: row.userObjectId,
      expiresAt: null
    });
  };

  const handleExtendSubmit = (expiresAt: string | null) => {
    if (!extendTarget?.entityId || !extendTarget.userObjectId) return;
    updateExpiryMutation.mutate({
      entityId: extendTarget.entityId,
      userObjectId: extendTarget.userObjectId,
      expiresAt
    });
  };

  const handleRevokeConfirm = () => {
    if (!revokeTarget?.entityId || !revokeTarget.userObjectId) return;
    revokeMutation.mutate({
      entityId: revokeTarget.entityId,
      userObjectId: revokeTarget.userObjectId
    });
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        flex: 1,
        minHeight: 0
      }}
    >
      <Stack
        direction={{ xs: "column", md: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <Autocomplete<TradingCounterPartySummary>
          sx={{ minWidth: 460 }}
          multiple={false}
          options={counterpartyOptions}
          value={selectedCounterparty}
          onChange={(_, next) => setSelectedCounterparty(next)}
          getOptionLabel={(option) =>
            option.counterpartyName
              ? `${option.counterpartyName} - ${option.tradingTeam ?? ""}`
              : ""
          }
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderValue={(v) => {
            return (
              <Box
                display="flex"
                gap={3}
                alignItems="center"
                paddingX="8px"
                height="100%"
              >
                <Typography variant="body1">{v?.counterpartyName}</Typography>-
                <Typography variant="body1">{v?.tradingTeam}</Typography>
              </Box>
            );
          }}
          loading={counterpartiesQuery.isLoading}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Counterparty"
              placeholder={selectedCounterparty ? "" : "Select a counterparty"}
            />
          )}
        />

        <Stack direction="row" spacing={2} alignItems="center">
          <FormControlLabel
            control={
              <Switch
                checked={includeRevoked}
                onChange={(e) => setIncludeRevoked(e.target.checked)}
              />
            }
            label="Show revoked"
          />
          <Button
            variant="contained"
            startIcon={<Add />}
            disabled={!counterpartyId}
            onClick={() => setGrantOpen(true)}
          >
            Grant access
          </Button>
        </Stack>
      </Stack>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        {counterpartyId ? (
          <GrantsTable
            rows={grants}
            loading={grantsQuery.isLoading || grantsQuery.isFetching}
            onExtendExpiry={setExtendTarget}
            onClearExpiry={handleClearExpiry}
            onRevoke={setRevokeTarget}
          />
        ) : (
          <Box sx={{ padding: 4 }}>
            <Typography variant="body2" color="text.secondary">
              Select a counterparty to manage trader access.
            </Typography>
          </Box>
        )}
      </Box>

      {counterpartyId && (
        <GrantDialog
          open={grantOpen}
          entityId={counterpartyId}
          onClose={() => setGrantOpen(false)}
          onSubmit={(body) => grantMutation.mutate(body)}
          isSubmitting={grantMutation.isPending}
        />
      )}

      <ExtendExpiryDialog
        open={extendTarget !== null}
        grant={extendTarget}
        onClose={() => setExtendTarget(null)}
        onSubmit={handleExtendSubmit}
        isSubmitting={updateExpiryMutation.isPending}
      />

      <RevokeConfirmDialog
        open={revokeTarget !== null}
        grant={revokeTarget}
        entityLabel="counterparty"
        onCancel={() => setRevokeTarget(null)}
        onConfirm={handleRevokeConfirm}
        isSubmitting={revokeMutation.isPending}
      />
    </Box>
  );
};
